import moment from "moment-timezone";
import request from "superagent";
import { SSO_CALLBACK_PATH } from "@src/appV2/Auth/RipplingSso/paths";
import { routes } from "@src/appV2/redesign/utils/routes";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";

import {
  FIREBASE_LOGIN_SUCCESS,
  get as fetchSession,
  LOGIN_REQUESTED,
  FIREBASE_TOKEN_READY,
  ADMIN_PROFILE_SUCCESS,
  LOGOUT,
  logout,
} from "./../session";
import { FirebaseAuth } from "../../utils/firebase";
import firebase from "firebase/app";
import { fetchAuthSessionWithLock } from "@src/appV2/Auth/cognito/cognitoSessionLock";

import { getFacilityUser } from "../../api/facilityUsers";
import { getAuthHeader } from "../..//utils/superagent";
import { removeDatadogRUMUser } from "../../utils/datadog";
import { isFirebaseSessionFallbackEnabled } from "@src/appV2/Auth/cognito/firebaseSessionFallbackState";
import { exchangeFirebaseTokenForCognitoSessionIfNeeded } from "@src/appV2/Auth/cognito/firebaseTokenExchange";
import {
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";

/**
 * @deprecated All call sites are gated behind `isFirebaseSessionFallbackEnabled()`.
 * Remove this function (and the Firebase-specific helpers in this file) once
 * `DISABLE_FIREBASE_SESSION_FALLBACK` is rolled out to 100%.
 */
export const initFirebaseSession = () => async (dispatch) => {
  /*
   * @Warning
   * onAuthStateChanged() listener causes onAuthChanged() function to be triggered twice, hence commented out
   */
  // FirebaseAuth.onAuthStateChanged(onAuthChanged(dispatch))

  // This method has the same behavior as firebase.auth.Auth.onAuthStateChanged had prior to 4.0.0. (Firebase Docs)
  const unsubscribe = FirebaseAuth.onIdTokenChanged(onAuthChanged(dispatch));
  await FirebaseAuth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
  // Note: ready:false is NOT dispatched here because initCognitoSession already
  // handles it. Re-dispatching ready:false would flicker the ready state back
  // to false after Cognito has already established the session.
  return unsubscribe;
};

const consumeOverrideUserIdUrlParam = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const userId = searchParams.get("overrideUserId");
  if (userId) {
    searchParams.delete("overrideUserId");
    history.replaceState(
      window.history.state,
      "",
      `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`
    );
    return userId;
  }

  // Check if overrideUserId is nested within the `requestedUrl` param
  const requestedUrlParam = searchParams.get("requestedUrl");
  if (requestedUrlParam) {
    try {
      const requestedUrl = new URL(requestedUrlParam, window.location.origin);
      const nestedParams = new URLSearchParams(requestedUrl.search);
      const nestedUserId = nestedParams.get("overrideUserId");

      if (nestedUserId) {
        return nestedUserId;
      }
    } catch {
      return null;
    }
  }
};

const onFacilityLogin = async ({ dispatch, user, tokenResult }) => {
  const facilityIdsArray = user?.facilities.map((facility) => facility.userId);

  const overrideUserId = consumeOverrideUserIdUrlParam();
  if (overrideUserId) {
    localStorage.setItem("selectedFacility", overrideUserId);
  }

  const selectedFacility = localStorage.getItem("selectedFacility");
  const selectedUser = localStorage.getItem("selectedUser");
  const isSelectedFacilityExists = selectedFacility && facilityIdsArray.includes(selectedFacility);
  const userId = isSelectedFacilityExists ? selectedFacility : facilityIdsArray[0] || selectedUser;

  // when facility user logins, selectedFacility is empty (we clear localStorage after logging out)
  if (!isSelectedFacilityExists) {
    localStorage.setItem("selectedFacility", facilityIdsArray[0]);
    logEvent(HCF_USER_EVENTS.LOGGED_IN, {
      workplaceId: userId,
      workplaceUserId: user?._id,
    });
  }

  dispatch(
    fetchSession({
      userId,
    })
  );

  dispatch({
    type: FIREBASE_LOGIN_SUCCESS,
    data: {
      firebaseToken: tokenResult.token,
      user: {
        facilityIds: facilityIdsArray,
        tmz: user.tmz,
        access: user.roles,
        permissions: user.permissions,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        notify: user.notify,
        isEmailVerified: user.isEmailVerified,
        isSuspicious: user.isSuspicious,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });
};

const onCBHAdminLoggedIn = async ({ dispatch, user, tokenResult }) => {
  const overrideUserId = consumeOverrideUserIdUrlParam();
  if (overrideUserId) {
    localStorage.setItem("selectedUser", overrideUserId);
  }

  const selectedUser = localStorage.getItem("selectedUser");

  dispatch({
    type: LOGIN_REQUESTED,
  });

  if (tokenResult) {
    dispatch(
      fetchSession({
        userId: selectedUser || user._id,
      })
    );
  }
  dispatch({
    type: FIREBASE_LOGIN_SUCCESS,
    data: {
      firebaseToken: tokenResult.token,
      user: {
        access: user.roles,
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        ...(user?.intercomHash && { intercomHash: user.intercomHash }),
      },
    },
  });
};

const handleEmployeeLogin = async (dispatch, tokenResult) => {
  const resp = await request.get(`${global.api}/user/getByEmail`).set(await getAuthHeader());

  dispatch({ type: ADMIN_PROFILE_SUCCESS, data: { user: resp.body } });

  const user = { ...resp.body };
  void onCBHAdminLoggedIn({ dispatch, user, tokenResult });
};

const handleFacilityLogin = async (dispatch, tokenResult) => {
  const { user } = await getFacilityUser();
  void onFacilityLogin({ dispatch, user, tokenResult });
};

const onLoggedIn = async (dispatch, user) => {
  const tokenResult = await user.getIdTokenResult();
  const { email } = tokenResult.claims;
  localStorage.setItem("AUTH_TOKEN", tokenResult.token);
  dispatch({
    type: FIREBASE_TOKEN_READY,
    data: {
      ready: true,
    },
  });
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  email?.includes("clipboardhealth.com")
    ? await handleEmployeeLogin(dispatch, tokenResult)
    : await handleFacilityLogin(dispatch, tokenResult);

  // After the Firebase session is fully established, exchange the Firebase
  // token for a Cognito session in the background. This runs fire-and-forget
  // so it does not block the UI — the user already has a working session.
  // Uses the shared guarded function to avoid racing with useEnsureCognitoSession.
  if (email) {
    setLoginIdentityProvider(IDENTITY_PROVIDER.FIREBASE_FALLBACK);
    void exchangeFirebaseTokenForCognitoSessionIfNeeded({
      email,
      firebaseIdToken: tokenResult.token,
    })
      .then((exchanged) => {
        if (exchanged) {
          logEvent(APP_V2_APP_EVENTS.FIREBASE_FALLBACK_COGNITO_EXCHANGE_SUCCESS, {
            outcome: LOGIN_OUTCOME.SUCCESS,
          });
        }
      })
      .catch((error: unknown) => {
        logError(APP_V2_APP_EVENTS.FIREBASE_FALLBACK_COGNITO_EXCHANGE_FAILURE, {
          error,
          metadata: {
            source: "firebase-fallback-exchange",
            outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
            reason: isNetworkFailure(error)
              ? LOGIN_REASON.NETWORK_FAILURE
              : LOGIN_REASON.FIREBASE_EXCHANGE_FAILED,
          },
        });
      });
  }
};

/**
 * @deprecated All call sites are gated behind Firebase-specific feature flags.
 * Remove this function once `DISABLE_FIREBASE_SESSION_FALLBACK` is rolled out to 100%.
 */
export const keepUserSessionAlive = async (dispatch, validTillInHours = 720) => {
  let authTime: string | undefined;

  if (!isFirebaseSessionFallbackEnabled()) {
    // When Firebase is disabled, check the Cognito token's auth_time claim.
    try {
      const session = await fetchAuthSessionWithLock();
      const authTimeSec = session.tokens?.idToken?.payload?.auth_time;
      if (typeof authTimeSec === "number") {
        authTime = new Date(authTimeSec * 1000).toISOString();
      }
    } catch {
      // No valid Cognito session — let the caller decide what to do.
      return;
    }
  } else {
    const currentUserToken = await FirebaseAuth.currentUser?.getIdTokenResult();
    authTime = currentUserToken?.authTime;
  }

  if (authTime && moment().diff(moment(authTime), "hours") >= validTillInHours) {
    dispatch(logout());
  }
};

const onLoggedOut = async (dispatch) => {
  dispatch({
    type: LOGOUT,
  });
  removeDatadogRUMUser();
};

const hasValidCognitoTokenInStorage = (): boolean => {
  const nowInSeconds = Math.floor(Date.now() / 1000);

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("CognitoIdentityServiceProvider") && key.endsWith(".idToken")) {
      const token = localStorage.getItem(key);
      if (!token) {
        continue;
      }

      try {
        const [, payload = ""] = token.split(".");
        const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
        const paddedPayload =
          normalizedPayload + "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
        const decoded = JSON.parse(atob(paddedPayload));

        if (typeof decoded.exp === "number" && decoded.exp > nowInSeconds) {
          return true;
        }
      } catch {
        // Malformed or stale token — skip and keep searching.
      }
    }
  }
  return false;
};

const onAuthChanged = (dispatch) => async (user) => {
  if (user) {
    await keepUserSessionAlive(dispatch);
    await onLoggedIn(dispatch, user);
  } else {
    // Before logging out, check if there's a valid (non-expired) Cognito
    // session. This prevents Firebase's onIdTokenChanged firing with null
    // (e.g., user logged in Cognito-only) from wiping a valid session.
    // Uses a synchronous localStorage check (decoding the JWT exp claim)
    // instead of the async fetchAuthSession() to avoid a race condition:
    // if the async check is in-flight when onIdTokenChanged fires again
    // with a valid user (e.g., E2E loginWithCognitoPassword), the delayed
    // onLoggedOut would destroy the session that onLoggedIn just established.
    if (hasValidCognitoTokenInStorage()) {
      return;
    }
    // Skip logout on the SSO callback page. The Amplify OAuth listener
    // is exchanging the authorization code for tokens asynchronously —
    // the Cognito session hasn't been written to localStorage yet.
    if (globalThis.location.pathname === SSO_CALLBACK_PATH) {
      return;
    }
    // Skip logout on /v2/ routes (magic link, password login, SSO callback,
    // etc.). During initiateCustomAuth(), Cognito's signOut() clears tokens
    // from localStorage before writing new ones, creating a ~50-200ms window
    // where hasValidCognitoTokenInStorage() returns false. If Firebase
    // initializes in that window and fires onIdTokenChanged(null), this
    // would incorrectly dispatch LOGOUT and kick the user out mid-auth.
    if (globalThis.location.pathname.startsWith(routes.appV2)) {
      return;
    }
    void onLoggedOut(dispatch);
  }
};
